<template>

  <div>
    <xolas-table
      title="Tickets"
      icon="fa fa-headset"

      table-ref="tickettable"
      ref="tickettable"

      :url="url"
      :fields="fields"

      sort-field="target"

      search-placeholder="Priority, Category, Type, Sub-Type..."

      :layer-component="(disableLayer || readOnly) ? null : 'ticket'"
      :layer-args="{ id: 'id' }">

      <template #actions>
        <ticket-action v-bind="addProps" />
      </template>

      <template #below-filter>
        <ticket-stats v-if="bottomComp" v-bind="bottomCompProps" />
      </template>

      <template #controls="props">
        <ticket-controls :row-data="props.rowData" />
      </template>

      <template #shortName="props">
        <org-badge :organisation="props.rowData.organisation" />
      </template>

      <template #contractor="props">
        <org-badge v-if="props.rowData.contractor" :organisation="props.rowData.contractor" />
      </template>

      <template #opened="props">
        <el-popover v-if="props.rowData.opened" trigger="hover" placement="left">
          <table v-if="props.rowData.opened || props.rowData.target">
            <tr v-if="props.rowData.opened"><td>Opened</td><td><b>{{formatDate(props.rowData.opened)}}</b></td></tr>
            <tr v-if="props.rowData.target"><td>Target</td><td><b>{{formatDate(props.rowData.target)}}</b></td></tr>
            <tr v-if="props.rowData.completedDate"><td>Completed</td><td><b>{{formatDate(props.rowData.completedDate)}}</b></td></tr>
          </table>
          <template #reference><el-button>{{props.rowData.days}}</el-button></template>
        </el-popover>
      </template>

    </xolas-table>

  </div>

</template>


<script>
import Vue from 'vue'

import SiteMixins from '@/mixins/SiteMixins'

import TicketControls from '@/components/tables/controls/TicketControls'
import TicketAction from '@/components/actions/TicketAction'
import TicketStats from '@/components/maintenance/TicketStats'
import XolasTable from '@/components/atoms/tables/XolasTable'

Vue.component('ticket-controls', TicketControls)

export default {

  mixins: [SiteMixins],

  components: {
    TicketAction,
    TicketControls,
    TicketStats,
    XolasTable
  },

  props: {
    status: {
      type: String,
      default: null
    },

    priority: {
      type: String,
      default: null
    },

    titleHeading: {
      type: String,
      default: 'h5'
    },

    siteId: {
      type: Number,
      default: null
    },

    assetId: {
      type: Number,
      default: null
    },

    ownerId: {
      type: Number,
      default: null
    },

    showOwner: {
      type: Boolean,
      default: true
    },

    hideSite: {
      type: Boolean,
      default: false
    },

    editMode: {
      type: Boolean,
      default: false
    },

    processKey: {
      type: String,
      default: 'ticket'
    },

    readOnly: {
      type: Boolean,
      default: false
    },

    bottomComp: {
      type: Object,
      default: null
    },

    bottomCompProps: {
      type: Object,
      default: null
    },

    disableLayer: {
      type: Boolean,
      default: false
    }
  },

  computed: {
    url: function () {
      // /tickets/site/{siteId}/assets/{assetId}/{orgId?}
      if (!this.status && !this.priority) {
        return '/tickets' +
        (this.siteId ? '/site/' + this.siteId : '') +
        (this.equipId ? '/assets/' + this.equipId
        : '')
      } else {
        return '/tickets/status/' + this.status +
        (this.priority ? '/' + this.priority : '') +
        (this.orgId ? '/' + this.orgId : '')
      }
    },

  },


  watch: {

    ownerId: function (id) {
//      console.log('watch ownerId', id)
      this.orgId = !this.isTowerCo() ? this.org().id : id
    },

    // The component is reused across /tickets/<status> filter routes without
    // remounting, so the computed `url` changes but XolasTable (which fetches on
    // mount / explicit refresh only) keeps showing stale rows. Refetch when the
    // url changes — same pattern as SitesTable/RequestTable. $nextTick so
    // <xolas-table> sees the new :url prop before refresh() fires. (XOL-230)
    url: function () {
      this.$nextTick(() => {
        this.$refs.tickettable && this.$refs.tickettable.refresh()
      })
    }

  },


  data () {
    return {
      orgId: this.isTelco() ? this.org().id : this.ownerId,
      addProps: {
        editMode: this.editMode,
        siteId: this.siteId,
        ownerId: this.ownerId,
        assetId: this.assetId,
        readOnly: this.readOnly,
        rowData: null
      },
      // Column widths mirror the legacy (data-table) layout on the dev server:
      // narrow content columns get a fixed width; Category and Site flex
      // (min-width) to absorb the remaining space, like the old auto-sized table.
      fields: [
        {
          name: 'status',
          title: 'Status',
          sortField: 'status',
          width: '142',
          callback: this.formatStatus
        },
        {
          name: 'category',
          title: 'Category',
          sortField: 'category',
          minWidth: '321',
          dataClass: 'text-center',
          callback: this.formatCategory
        },
        {
          name: 'priority',
          title: 'Priority',
          sortField: 'priority',
          width: '103',
          titleClass: 'hidden-sm-and-down',
          dataClass: 'hidden-sm-and-down text-center',
          callback: this.formatPriority
        },
        {
          name: 'site.siteName',
          title: 'Site',
          minWidth: '293',
          titleClass: 'hidden-sm-and-down',
          dataClass: 'hidden-sm-and-down',
          visible: !this.hideSite
        },
        {
          name: '__slot:opened',
          title: 'Days Open',
          width: '111'
        },
        {
          name: '__slot:shortName',
          title: 'Reported by',
          width: '123',
          titleClass: 'hidden-md-and-down',
          dataClass: 'hidden-md-and-down',
          visible: this.showOwner
        },
        {
          name: '__slot:contractor',
          title: 'Contractor',
          width: '107',
          titleClass: 'hidden-md-and-down',
          dataClass: 'hidden-md-and-down'
        },
        {
          name: '__slot:controls',
          title: '',
          align: 'center',
          width: '125',
          titleClass: 'hidden-sm-and-down',
          dataClass: 'hidden-sm-and-down'
        }
      ]
    }
  }   // data

}

</script>
